import './Article.less';

import React, { useEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import Markdown from 'nike-ui/src/components/markdown/Markdown';
import { Menu } from 'nike-ui/src/components/menu/Menu';
import { useScrollToAnchorOnInit } from 'nike-ui/src/common/routing/useScrollToAnchor';
import { NikeRouteComponentProps } from 'nike-ui/src/common/routing/types';
import BlockLoader from '@/ui/BlockLoader';
import ArticleIcon from '@/icons/ArticleIcon';
import BoxStoreContext from '@/markdown/modules/BoxStoreContext';
import { MdForumContextProvider } from '@/markdown/modules/MdForumContext';
import { DialogBody, Docked } from '@/ui/dialogs/Dialog';
import { loadMarkdownInternalArticle, loadMarkdownMarketingArticle } from '@/article/handlers';
import { ArticleMenu } from './ArticleMenu';

function Article({
    articleCode,
    articleNiceUrl,
    type,
    ignoreCache,
    className,
    emptyState,
    isPromoPage
}: {
    articleCode?: string;
    articleNiceUrl?: string;
    type?: string;
    ignoreCache?: boolean;
    className?: string;
    emptyState?: JSX.Element;
    isPromoPage?: boolean;
}) {
    useEffect(() => {
        if (type === 'marketing') {
            dispatch(loadMarkdownMarketingArticle(articleCode, ignoreCache, isPromoPage));
        } else {
            // internal
            dispatch(loadMarkdownInternalArticle(articleCode));
        }
    }, [articleCode, articleNiceUrl, type, ignoreCache, isPromoPage]);

    const derefSub = useReframe();

    let article = derefSub(['markdown.article', articleCode]);

    useScrollToAnchorOnInit({
        hash: document.location.hash,
        enabled: article && article.get('id') && article.get('text'),
        scrollOffset: 50
    });

    if (!article) {
        // @ts-ignore
        return <BlockLoader />;
    } else if (!article.get('id')) {
        const stateIcon = <ArticleIcon width={60} height={60} fill="#aab2bd" opacity={1} />;
        // @ts-ignore
        return emptyState ? emptyState : <EmptyState stateIcon={stateIcon} />;
    }

    return (
        <MdForumContextProvider subjectId={article.get('url')} subjectType="ARTICLE">
            <BoxStoreContext.Provider value={`article-${articleCode}`}>
                <Markdown className={className} article>
                    {article.get('text')}
                </Markdown>
            </BoxStoreContext.Provider>
        </MdForumContextProvider>
    );
}

export default Article;

export function ArticleDialog({ articleCode, type }) {
    return (
        <Docked>
            <DialogBody>
                <Article articleCode={articleCode} type={type} />
            </DialogBody>
        </Docked>
    );
}

/**
 *  Root path component; params: []
 */
export function article(niceUrl: string, menu?: Menu) {
    return (props: NikeRouteComponentProps) => (
        <>
            <Article articleCode={niceUrl} type="marketing" />
            <ArticleMenu menu={menu} />
        </>
    );
}

/**
 *  Root path component; params: [articleCode, ignoreCache]
 */
export function ParamArticle({ articleCode, ignoreCache }: { articleCode: string; ignoreCache? }) {
    return (
        <Article articleCode={articleCode} type="marketing" ignoreCache={ignoreCache} key={'article' + articleCode} />
    );
}
